<script>
import { DateTime } from 'luxon';
import Event from '@components/Event';
import EventSkeleton from '@components/EventSkeleton';
import { normalizeEventFromAPI, toUserZone, formatEventDate } from '@utils/timezone';

// The athlete's financial view. The «Υπόλοιπο» balance strip stays pinned
// above three tabs sharing one row UI: «Μελλοντικές» (upcoming, soonest
// first) / «Ολοκληρωμένες» (month-grouped, recent first, show-more capped)
// with inline pay forms, and a right-aligned «Οφειλές» (remainders
// management). The tabs are pure filters — content renders below the
// (empty) b-tabs off tabIndex. Fetches on mount and refetches after every
// mutation. Hosted by AthleteFinancialsPanel and embedded in
// CalendarEventPanel (in-panel Οικονομικά view).
const PAGE_SIZE = 10;

export default {
  name: 'AthleteFinancialsContent',
  components: { Event, EventSkeleton },

  props: {
    athleteId: { type: String, required: true },
  },

  data() {
    return {
      loading: false,
      loaded: false,
      owed: 0,
      events: [],
      remainders: [],
      pastRemainders: [],
      showClosed: false,
      showAddForm: false,
      newAmount: null,
      newNotes: '',
      submitting: false,
      payFormEventId: null,
      payAmount: null,
      payMethod: null,
      settleFormId: null,
      settleMethod: null,
      paymentMethods: ['cash', 'card', 'transfer'],
      visibleCount: PAGE_SIZE,
      tabIndex: 0,
    };
  },

  computed: {
    hiddenCount() {
      return Math.max(0, this.pastEvents.length - this.visibleCount);
    },
    // Same split the old history modal had: normalized start vs now.
    upcomingEvents() {
      const now = DateTime.now().toMillis();
      return this.events
        .filter(event => event.start && DateTime.fromISO(event.start).toMillis() > now)
        .sort((a, b) => DateTime.fromISO(a.start).toMillis() - DateTime.fromISO(b.start).toMillis());
    },
    pastEvents() {
      const now = DateTime.now().toMillis();
      return this.events.filter(event => !event.start || DateTime.fromISO(event.start).toMillis() <= now);
    },
    // The list the active session tab shows: upcoming as one unlabeled
    // group, past as month groups over the show-more-capped slice.
    displayedGroups() {
      if (this.tabIndex === 0) {
        if (!this.upcomingEvents.length) return [];
        return [{ id: 'future', label: null, events: this.upcomingEvents }];
      }
      const groups = [];
      for (const event of this.pastEvents.slice(0, this.visibleCount)) {
        const label = event.start
          // toLocaleString without a day gives the nominative month in Greek
          // («Ιούλιος») — toFormat('LLLL') renders the genitive («Ιουλίου»).
          ? toUserZone(event.start).setLocale(this.$i18n.locale).toLocaleString({ month: 'long', year: 'numeric' })
          : '—';
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.events.push(event);
        else groups.push({ id: label, label, events: [event] });
      }
      return groups;
    },
  },

  created() {
    this.fetchFinancials();
  },

  methods: {
    async fetchFinancials() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get(`/financials/athletes/${this.athleteId}`);
        this.owed = data.owed;
        // Same normalization the appointment history applies, so the shared
        // Event row renders these identically.
        this.events = (data.events || []).map(normalizeEventFromAPI);
        this.remainders = data.remainders || [];
        this.pastRemainders = data.pastRemainders || [];
      } catch (e) {
        this.emitErrorNotification();
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },

    formatDate(date) {
      return date ? DateTime.fromISO(date).toFormat('dd/MM/yyyy') : '';
    },

    // 'none' | 'paid' | 'unpaid'
    statusFor(event) {
      return event.paymentStatus || 'none';
    },

    paidTooltip(event) {
      return [
        this.formatDate(event.paidAt),
        event.paymentMethod ? this.$t(`financials.methods.${event.paymentMethod}`) : null,
      ].filter(Boolean).join(' · ');
    },

    remainderTitle(remainder) {
      return remainder.notes || this.formatDate(remainder.createdAt);
    },

    closedMeta(remainder) {
      return [
        this.formatDate(remainder.settledAt || remainder.updatedAt),
        remainder.settledMethod ? this.$t(`financials.methods.${remainder.settledMethod}`) : null,
      ].filter(Boolean).join(' · ');
    },

    // One-click paths: 'unpaid' and undo (null). Paid goes through the
    // inline pay form so amount/method/partial can be recorded.
    async setEventPayment(event, status) {
      try {
        await this.$axios.put(`/calendar/${event.id}/payment`, { status });
        this.emitSuccessNotification();
        this.fetchFinancials();
      } catch (e) {
        this.emitErrorNotification();
      }
    },

    openPayForm(event) {
      this.payFormEventId = event.id;
      this.payAmount = event.sessionCost || null;
      this.payMethod = null;
    },

    payRemainder(event) {
      const cost = parseInt(event.sessionCost) || 0;
      const amount = parseInt(this.payAmount, 10) || 0;
      if (!cost || amount <= 0 || amount >= cost) return 0;
      return cost - amount;
    },

    canSubmitPay(event) {
      const cost = parseInt(event.sessionCost) || 0;
      if (!cost) return true;
      const amount = parseInt(this.payAmount, 10);
      return amount > 0 && amount <= cost;
    },

    async submitPaid(event) {
      const body = { status: 'paid', method: this.payMethod || undefined };
      const cost = parseInt(event.sessionCost) || 0;
      if (cost) {
        const amount = parseInt(this.payAmount, 10);
        if (!(amount > 0) || amount > cost) return;
        if (amount < cost) {
          // Remainders are standalone athlete-level entries — the session
          // context travels as a plain note.
          body.remainder = {
            amount: cost - amount,
            notes: `${this.$t('financials.session')} ${formatEventDate(event.start)}`,
          };
        }
      }
      this.submitting = true;
      try {
        await this.$axios.put(`/calendar/${event.id}/payment`, body);
        this.emitSuccessNotification({ text: this.$t('financials.paymentRecorded') });
        this.payFormEventId = null;
        this.fetchFinancials();
      } catch (e) {
        this.emitErrorNotification();
      } finally {
        this.submitting = false;
      }
    },

    async setRemainderStatus(remainder, status) {
      try {
        await this.$axios.put(`/financials/remainders/${remainder.id}`, { status });
        this.emitSuccessNotification();
        this.fetchFinancials();
      } catch (e) {
        this.emitErrorNotification();
      }
    },

    openSettleForm(remainder) {
      this.settleFormId = remainder.id;
      this.settleMethod = null;
    },

    async submitSettle(remainder) {
      this.submitting = true;
      try {
        await this.$axios.put(`/financials/remainders/${remainder.id}`, {
          status: 'settled',
          method: this.settleMethod || undefined,
        });
        this.emitSuccessNotification({ text: this.$t('financials.paymentRecorded') });
        this.settleFormId = null;
        this.fetchFinancials();
      } catch (e) {
        this.emitErrorNotification();
      } finally {
        this.submitting = false;
      }
    },

    deleteRemainder(remainder) {
      this.confirmDelete(async (dialog) => {
        try {
          await this.$axios.delete(`/financials/remainders/${remainder.id}`);
          this.emitSuccessNotification();
          this.fetchFinancials();
        } catch (e) {
          this.emitErrorNotification();
        } finally {
          dialog.close();
        }
      });
    },

    async addRemainder() {
      const amount = parseInt(this.newAmount, 10);
      if (!amount || amount <= 0) return;

      this.submitting = true;
      try {
        await this.$axios.post(`/financials/athletes/${this.athleteId}/remainders`, {
          amount,
          notes: this.newNotes.trim() || undefined,
        });
        this.showAddForm = false;
        this.newAmount = null;
        this.newNotes = '';
        this.emitSuccessNotification();
        this.fetchFinancials();
      } catch (e) {
        this.emitErrorNotification();
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<template>
  <div class="athlete-financials-content" data-testid="financials">
    <!-- Skeleton of the strip, tabs and rows below, on their own classes. -->
    <div v-if="!loaded" aria-busy="true" class="tw-pointer-events-none">
      <div class="fin-owed tw-mb-3">
        <span class="fin-owed__label tw-flex tw-items-center">&#8203;<b-skeleton width="70px" height="0.8em" class="tw-mb-0"/></span>
        <span class="fin-owed__amount tw-flex tw-items-center">&#8203;<b-skeleton width="48px" height="0.8em" class="tw-mb-0"/></span>
      </div>
      <b-tabs nav-class="nav-tabs nav-bordered">
        <b-tab :title="$t('financials.futureTab')" />
        <b-tab :title="$t('financials.pastTab')" />
        <b-tab title-item-class="tw-ml-auto">
          <template #title><i class="fe-credit-card mr-1" />{{ $t('financials.owedTab') }}</template>
        </b-tab>
      </b-tabs>
      <div class="tw-mt-2">
        <div v-for="width in ['45%', '35%', '50%']" :key="width" class="fin-session">
          <event-skeleton :width="width">
            <template #append>
              <span class="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                <b-skeleton width="36px" height="0.8em" class="tw-mb-0"/>
                <b-skeleton width="24px" height="24px" class="tw-mb-0 tw-rounded-md"/>
              </span>
            </template>
          </event-skeleton>
        </div>
      </div>
    </div>

    <!-- Mirrored by the skeleton above -->
    <div v-if="loaded" class="fin-owed tw-mb-3" :class="{ 'fin-owed--due': owed > 0 }">
      <span class="fin-owed__label">{{ $t('financials.balance') }}</span>
      <span class="fin-owed__amount" data-testid="financials-balance">{{ owed }} €</span>
    </div>

    <b-tabs v-if="loaded" v-model="tabIndex" nav-class="nav-tabs nav-bordered">
      <b-tab :title="$t('financials.futureTab')" />
      <b-tab :title="$t('financials.pastTab')" :title-link-attributes="{ 'data-testid': 'financials-past-tab' }" />
      <b-tab title-item-class="tw-ml-auto" :title-link-attributes="{ 'data-testid': 'financials-owed-tab' }">
        <template #title>
          <i class="fe-credit-card mr-1" />{{ $t('financials.owedTab') }}<span v-if="owed > 0" class="fin-tab-dot" />
        </template>
      </b-tab>
    </b-tabs>

    <div v-if="loaded" class="tw-mt-2">
      <template v-if="tabIndex !== 2">
        <template v-for="group in displayedGroups">
          <div v-if="group.label" :key="group.id" class="fin-month">{{ group.label }}</div>

          <div v-for="event in group.events" :key="event.id" class="fin-session">
            <Event :event="event">
              <template v-if="!event.isCancelled" #append>
                <span class="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                  <span v-if="event.sessionCost" class="fin-row__amount"
                        :class="statusFor(event) === 'unpaid' ? 'text-danger' : ''"
                  >{{ event.sessionCost }} €</span>

                  <b-badge v-if="statusFor(event) === 'paid'" variant="success" class="fin-chip"
                           v-b-tooltip :title="paidTooltip(event)"
                  >{{ $t('financials.markPaid') }}</b-badge>
                  <b-badge v-if="statusFor(event) === 'unpaid'" variant="danger" class="fin-chip">{{ $t('financials.markUnpaid') }}</b-badge>

                  <template v-if="event.sessionCost">
                    <b-button v-if="statusFor(event) === 'none' || statusFor(event) === 'unpaid'"
                              size="xs" variant="outline-primary" class="fin-action"
                              v-b-tooltip :title="$t('financials.recordPayment')" data-testid="record-payment"
                              @click="openPayForm(event)"
                    ><i class="fe-check" /></b-button>

                    <b-button v-if="statusFor(event) === 'none'"
                              size="xs" variant="outline-danger" class="fin-action"
                              v-b-tooltip :title="$t('financials.markUnpaid')"
                              @click="setEventPayment(event, 'unpaid')"
                    ><i class="fe-x" /></b-button>

                    <b-button v-if="statusFor(event) !== 'none'"
                              size="xs" variant="link" class="fin-action fin-action--quiet"
                              v-b-tooltip :title="$t('financials.undoPayment')"
                              @click="setEventPayment(event, null)"
                    ><i class="fe-rotate-ccw" /></b-button>
                  </template>
                </span>
              </template>
            </Event>

            <form v-if="payFormEventId === event.id" class="fin-payform"
                  @submit.prevent="submitPaid(event)"
            >
              <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <b-input-group v-if="event.sessionCost" append="€" size="sm" class="fin-payform__amount">
                  <b-form-input v-model="payAmount" type="number" min="1" :max="event.sessionCost" step="1" data-testid="pay-amount" />
                </b-input-group>
                <b-button v-for="paymentMethod in paymentMethods" :key="paymentMethod" size="sm"
                          :variant="payMethod === paymentMethod ? 'primary' : 'light'" :data-testid="`pay-method-${paymentMethod}`"
                          @click="payMethod = payMethod === paymentMethod ? null : paymentMethod"
                >{{ $t(`financials.methods.${paymentMethod}`) }}</b-button>

                <span class="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                  <b-button size="sm" variant="success" type="submit" :disabled="submitting || !canSubmitPay(event)" data-testid="pay-submit">
                    <i class="fe-check mr-1" />{{ $t('financials.recordPayment') }}
                  </b-button>
                  <b-button size="sm" variant="link" class="fin-action fin-action--quiet" @click="payFormEventId = null">
                    {{ $t('cancel') }}
                  </b-button>
                </span>
              </div>
              <p v-if="payRemainder(event)" class="fin-payform__notice">
                {{ $t('financials.remainderNotice', { amount: payRemainder(event) }) }}
              </p>
            </form>
          </div>
        </template>

        <div v-if="tabIndex === 1 && hiddenCount" class="tw-text-center tw-mt-2">
          <b-button size="sm" variant="link" class="fin-action fin-action--quiet" @click="visibleCount += 20">
            {{ $t('financials.showMore', { count: hiddenCount }) }} <i class="fe-chevron-down" />
          </b-button>
        </div>

        <empty-state v-if="tabIndex === 0 && !upcomingEvents.length && !loading" compact icon="fe-calendar" :title="$t('pages.athlete.events.emptyFuture')" :subtitle="$t('pages.athlete.events.emptyFutureHint')"/>
        <empty-state v-if="tabIndex === 1 && !pastEvents.length && !loading" compact icon="fe-clock" :title="$t('pages.athlete.events.emptyPast')" :subtitle="$t('pages.athlete.events.emptyPastHint')"/>
      </template>

      <template v-if="tabIndex === 2">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <div class="fin-section">{{ $t('financials.remainders') }}</div>
          <b-button size="xs" variant="outline-primary" class="tw-whitespace-nowrap" @click="showAddForm = !showAddForm">
            <i class="fe-plus" /> {{ $t('financials.addRemainder') }}
          </b-button>
        </div>

        <form v-if="showAddForm" class="fin-payform tw-mb-3" @submit.prevent="addRemainder">
          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <b-form-input v-model="newAmount" type="number" min="1" step="1" size="sm"
                          :placeholder="$t('financials.amountLabel')" class="fin-payform__amount"
            />
            <b-form-input v-model="newNotes" size="sm" :placeholder="$t('financials.notesPlaceholder')" class="tw-flex-1" />
            <b-button size="sm" variant="primary" type="submit" :disabled="submitting || !newAmount">
              {{ $t('financials.submitRemainder') }}
            </b-button>
            <b-button size="sm" variant="link" class="fin-action fin-action--quiet" @click="showAddForm = false">
              {{ $t('cancel') }}
            </b-button>
          </div>
        </form>

        <div v-for="remainder in remainders" :key="remainder.id" class="fin-session">
          <div class="fin-row">
            <div class="fin-row__main">
              <div class="fin-row__title">{{ remainderTitle(remainder) }}</div>
              <div v-if="remainder.notes" class="fin-row__meta">{{ $t('financials.created') }} · {{ formatDate(remainder.createdAt) }}</div>
            </div>
            <span class="fin-row__amount text-danger">{{ remainder.amount }} €</span>
            <b-button size="xs" variant="outline-primary" class="fin-action"
                      v-b-tooltip :title="$t('financials.settle')"
                      @click="openSettleForm(remainder)"
            ><i class="fe-check" /></b-button>
            <b-button size="xs" variant="link" class="fin-action fin-action--quiet"
                      v-b-tooltip :title="$t('financials.deleteEntry')" data-testid="remainder-delete"
                      @click="deleteRemainder(remainder)"
            ><i class="fe-trash-2" /></b-button>
          </div>

          <form v-if="settleFormId === remainder.id" class="fin-payform"
                @submit.prevent="submitSettle(remainder)"
          >
            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <b-button v-for="paymentMethod in paymentMethods" :key="paymentMethod" size="sm"
                        :variant="settleMethod === paymentMethod ? 'primary' : 'light'"
                        @click="settleMethod = settleMethod === paymentMethod ? null : paymentMethod"
              >{{ $t(`financials.methods.${paymentMethod}`) }}</b-button>

              <span class="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                <b-button size="sm" variant="success" type="submit" :disabled="submitting">
                  <i class="fe-check mr-1" />{{ $t('financials.settle') }}
                </b-button>
                <b-button size="sm" variant="link" class="fin-action fin-action--quiet" @click="settleFormId = null">
                  {{ $t('cancel') }}
                </b-button>
              </span>
            </div>
          </form>
        </div>

        <p v-if="!remainders.length && !showAddForm" class="fin-empty">{{ $t('financials.noRemainders') }}</p>

        <button v-if="pastRemainders.length" type="button" class="fin-closed-toggle" @click="showClosed = !showClosed">
          {{ $t('financials.closedRemainders') }} ({{ pastRemainders.length }})
          <i :class="showClosed ? 'fe-chevron-up' : 'fe-chevron-down'" />
        </button>

        <template v-if="showClosed">
          <div v-for="remainder in pastRemainders" :key="remainder.id" class="fin-row fin-row--closed">
            <div class="fin-row__main">
              <div class="fin-row__title">{{ remainderTitle(remainder) }}</div>
              <div class="fin-row__meta">{{ closedMeta(remainder) }}</div>
            </div>
            <span class="fin-row__amount">{{ remainder.amount }} €</span>
            <b-button size="xs" variant="link" class="fin-action fin-action--quiet"
                      @click="setRemainderStatus(remainder, 'open')"
            >{{ $t('financials.reopen') }}</b-button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.athlete-financials-content {
  .fin-owed {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.6rem;
    border-radius: 12px;
    padding: 0.65rem 1rem;
    background: $color_light_bg;

    &--due {
      background: rgba(220, 53, 69, 0.07);

      .fin-owed__label,
      .fin-owed__amount {
        color: #b02a37;
      }
    }

    &__label {
      font-size: 0.82rem;
      font-weight: 600;
      color: $color_primary_muted;
    }

    &__amount {
      font-size: 1.15rem;
      font-weight: 700;
    }
  }

  // .fin-row / .fin-chip / .fin-action / .fin-empty live in
  // design/custom/components/_financial-rows.scss.

  .fin-tab-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-left: 5px;
    vertical-align: middle;
    background: #dc3545;
  }

  .fin-section {
    font-size: 0.8rem;
    font-weight: 700;
  }

  .fin-month {
    font-size: 0.72rem;
    font-weight: 700;
    color: $color_primary_muted;
    margin: 0.85rem 0 0.35rem;

    &:first-child {
      margin-top: 0.25rem;
    }
  }

  .fin-session {
    margin-bottom: 0.5rem;

    .event-row,
    .fin-row {
      margin-bottom: 0;
    }

    .fin-payform {
      margin: 0.35rem 0 0;
    }
  }

  .fin-payform {
    margin: 0.35rem 0 0.5rem;
    padding: 0.5rem 0.6rem;
    background: $color_light_bg;
    border-radius: 10px;

    &__amount {
      max-width: 110px;
    }

    &__notice {
      font-size: 0.76rem;
      color: $color_primary_muted;
      margin: 0.5rem 0 0;
    }
  }

  .fin-closed-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border: 0;
    background: transparent;
    padding: 0.35rem 0.25rem 0;
    margin-top: 0.35rem;
    font-size: 0.76rem;
    font-weight: 600;
    color: $color_primary_muted;

    &:hover {
      color: $color_text;
    }
  }
}
</style>
